/**
 * Department-user view — the ONLY Sentinel surface a department user sees.
 * They fulfil records requests routed to their team. They see no case, no
 * subject, no allegation — only what was asked of them.
 */
import { useState, useEffect, useCallback } from 'react';
import { api, Spinner, EmptyState, Hint, Tooltip, dateShort } from './ui';

export default function MyRecordRequests({ user }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api('/my-requests').then((j) => setRequests(j.requests || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const respond = async (id, available) => {
    let note = '';
    if (available) note = window.prompt('Add a note for the investigator (optional) — e.g. "uploaded to the secure portal" or "attached".') || '';
    setBusyId(id);
    try { await api(`/my-requests/${id}/fulfill`, { method: 'POST', body: { available, note } }); load(); }
    catch (e) { alert(e.message); } finally { setBusyId(null); }
  };

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <div className="rounded-2xl bg-blue-50 border border-blue-100 px-5 py-4 mb-5 text-sm text-blue-900/90 leading-relaxed">
        <strong>Records requested from your team.</strong> The compliance team has asked your department for some
        records. Provide what you can, or let them know if your team doesn't have them. You won't see case details —
        just what's being asked.
      </div>

      <h2 className="font-semibold text-slate-800 flex items-center mb-3">Open requests<Hint text="Each item is a request for records routed to your department. Mark it provided once you've supplied the records, or flag that your team doesn't have them." /></h2>

      {loading ? <Spinner /> : requests.length === 0 ? (
        <EmptyState title="You're all caught up" hint="There are no open records requests for your team right now." />
      ) : (
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-slate-200 p-4">
              <p className="text-slate-800">{r.requested_item}</p>
              <p className="text-xs text-slate-400 mt-1">Requested {dateShort(r.created_at)}{r.due_at ? ` · please respond by ${dateShort(r.due_at)}` : ''}</p>
              <div className="mt-3 flex gap-2">
                <Tooltip text="Confirm you've provided the records. This lets the investigator know they've arrived.">
                  <button onClick={() => respond(r.id, true)} disabled={busyId === r.id}
                    className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50">I've provided these</button>
                </Tooltip>
                <Tooltip text="Tell the investigator your team doesn't have these records, so they can look elsewhere.">
                  <button onClick={() => respond(r.id, false)} disabled={busyId === r.id}
                    className="px-3 py-1.5 rounded-lg bg-white border border-slate-300 text-slate-600 text-sm font-medium hover:bg-slate-50 disabled:opacity-50">We don't have these</button>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
